import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import axios, { API, authHeaders, formatError } from "../../lib/api";

export const HolidaysManager = ({ onSaved }) => {
  const [list, setList] = useState([]);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const today = new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/holidays`, { headers: authHeaders() });
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/holidays`, { date, reason }, { headers: authHeaders() });
      setList(data);
      setDate("");
      setReason("");
      toast.success("Closed day added");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const remove = async (d) => {
    try {
      const { data } = await axios.delete(`${API}/admin/holidays/${d}`, { headers: authHeaders() });
      setList(data);
      toast.success("Closed day removed");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div data-testid="admin-holidays" className="mt-5 rounded-[24px] border hairline bg-[var(--forest-2)] p-6 sm:p-8">
      <p className="font-display text-2xl font-semibold">Closed days</p>
      <p className="text-sm text-[var(--sage)]">Upcoming holidays or shutdowns. Buyers see them on the website, in the order form and on the Contact page.</p>
      <form onSubmit={add} className="mt-5 grid sm:grid-cols-[180px_1fr_auto] gap-3 items-end">
        <div className="space-y-1.5"><Label htmlFor="h-date">Date</Label><Input id="h-date" data-testid="admin-holiday-date" type="date" min={today} required value={date} onChange={(e) => setDate(e.target.value)} className="h-11" /></div>
        <div className="space-y-1.5"><Label htmlFor="h-reason">Reason (optional, shown as typed)</Label><Input id="h-reason" data-testid="admin-holiday-reason" maxLength={80} placeholder="e.g. Chhath Puja" value={reason} onChange={(e) => setReason(e.target.value)} className="h-11" /></div>
        <button data-testid="admin-holiday-add" className="h-11 px-6 rounded-full bg-[var(--husk)] text-[var(--forest)] font-semibold">Add</button>
      </form>
      <ul className="mt-5">
        {list.length === 0 && <li data-testid="admin-holidays-empty" className="text-sm text-[var(--sage)]">No upcoming closed days.</li>}
        {list.map((h) => (
          <li key={h.date} data-testid={`admin-holiday-${h.date}`} className="flex items-center gap-3 border-t hairline py-3 text-sm">
            <span className="w-36 shrink-0">{new Date(`${h.date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</span>
            <span className="flex-1 text-[var(--sage)] truncate">{h.reason || "—"}</span>
            <button data-testid={`admin-holiday-delete-${h.date}`} onClick={() => remove(h.date)} aria-label="Remove closed day" className="grid h-9 w-9 place-items-center rounded-full border hairline hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};
